import { captureError } from '@repo/common/utils/web-tracing';
import type { ListBoxProps } from '@repo/foundations';
import { capitalize } from 'lodash-es';
import { findParentNode } from 'prosemirror-utils';
import type { EditorView } from 'prosemirror-view';
import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import type {
	EditorDictionary,
	EmbedDescriptor,
	ISecodaEditor,
	MenuItem,
} from '../..';
import insertFiles from '../../commands/insertFiles';
import type { CommandFactory } from '../../lib/Extension';
import Node from '../../nodes/Node';
import { getEventFiles } from '../../utils/files';

export interface UseCommandMenuProps {
	view: EditorView;
	commands: Record<string, CommandFactory>;
	dictionary: EditorDictionary;
	onClearSearch: () => void;
	onClose: () => void;
	onLinkToolbarOpen?: () => void;
	onPlaceholderToolbarOpen?: () => void;
	uploadFile?: (file: File, isImage: boolean) => Promise<string>;
	onFileUploadStart?: () => void;
	onFileUploadStop?: () => void;
	onShowToast?: (message: string, id: string) => void;
	onTrackEvent?: (
		eventName: string,
		properties?: Record<string, string>
	) => void;
	isTemplate?: boolean;
	editor: ISecodaEditor;
	targetRef: ListBoxProps['targetRef'];
}

export function useCommandMenu({
	view,
	commands,
	dictionary,
	onClearSearch,
	onClose,
	onLinkToolbarOpen,
	onPlaceholderToolbarOpen,
	uploadFile,
	onFileUploadStart,
	onFileUploadStop,
	onShowToast,
	onTrackEvent,
	isTemplate,
	editor,
	targetRef,
}: UseCommandMenuProps) {
	const [embedItem, setEmbedItem] = useState<EmbedDescriptor | null>();
	const [preselectionWidget, setPreselectionWidget] =
		useState<ReactNode | null>(null);

	const fileUploadInputRef = useRef<HTMLInputElement>(null);

	const insertBlock = useCallback(
		(item: MenuItem) => {
			onClearSearch();

			let command = item.name ? commands[item.name] : undefined;

			if (command) {
				command(item.attrs);
			} else {
				const commandName = `create${capitalize(item.name)}`;
				command = commands[commandName];
				if (command) {
					command(item.attrs);
				} else {
					captureError(new Error(`Command not found for ${commandName}`));
				}
			}

			onClose();
		},
		[commands, onClearSearch, onClose]
	);

	function triggerFilePick(accept: string) {
		if (fileUploadInputRef.current) {
			if (accept) {
				fileUploadInputRef.current.accept = accept;
			}
			fileUploadInputRef.current.click();
		}
	}

	async function handleFilePicked(event: React.ChangeEvent<HTMLInputElement>) {
		// Re-focus the editor as it loses focus when file picker is opened on iOS
		view.focus();

		const files = getEventFiles(event);
		const parent = findParentNode((node) => !!node)(view.state.selection);

		onClearSearch();

		if (!uploadFile) {
			throw new Error('uploadFile prop is required to replace files');
		}

		if (parent) {
			await insertFiles(view, event, parent.pos, files, {
				uploadFile,
				onFileUploadStart,
				onFileUploadStop,
				onShowToast,
				dictionary: dictionary,
				isAttachment: fileUploadInputRef.current?.accept === '*',
			});
		}

		if (fileUploadInputRef.current) {
			fileUploadInputRef.current.value = '';
		}

		onClose();
	}

	const handleItemClick = useCallback(
		(item: MenuItem) => {
			onTrackEvent?.('editor/insert-item', {
				itemName: item.name ?? 'unknown',
				itemTitle: item.title ?? '',
				location: window.location.pathname,
			});

			const node = editor.extensions.allExtensions.find(
				(extension) => extension.name === item.name
			);
			if (node && node instanceof Node && node.preselectionWidget) {
				const widget = node.preselectionWidget({
					onClose,
					targetRef,
				});
				if (widget) {
					setPreselectionWidget(widget);
					return;
				}
			}

			switch (item.name) {
				case 'image':
					triggerFilePick('image/*');
					break;
				case 'attachment':
					triggerFilePick('*');
					break;
				case 'embed':
					setEmbedItem(item as EmbedDescriptor);
					break;
				case 'link': {
					onClearSearch();
					onClose();
					onLinkToolbarOpen?.();
					break;
				}
				case 'placeholder': {
					onClearSearch();
					onClose();
					onPlaceholderToolbarOpen?.();
					break;
				}
				default:
					insertBlock(item);
					onClose();
					break;
			}
		},
		[
			insertBlock,
			onClearSearch,
			onClose,
			onLinkToolbarOpen,
			onPlaceholderToolbarOpen,
			onTrackEvent,
			editor,
		]
	);

	// const handleChartBlockV2Picked = useCallback(
	// 	async (result: CreateGraphModalResult) => {
	// 		const { state } = view;

	// 		const parent = findParentNode((node) => !!node)(state.selection);

	// 		if (parent) {
	// 			insertChartBlock(view, parent.pos, {
	// 				id: result.metric.id,
	// 				xAxis: result.xAxis,
	// 				yAxis: result.yAxis,
	// 				dimension: result.dimension,
	// 				numericFormat: result.numericFormat,
	// 			});
	// 		}

	// 		onClose();
	// 	},
	// 	[onClose, view]
	// );

	return {
		fileUploadInputRef,
		handleFilePicked,
		handleItemClick,
		embedItem,
		insertBlock,
		preselectionWidget,
	};
}
